import i18next from 'i18next';
import { initReactI18next } from 'react-i18next';
import resourcesToBackend from 'i18next-resources-to-backend';

// English bundled synchronously
import enCommon from '@/locales/en/common.json';
import enNav from '@/locales/en/nav.json';
import enAuth from '@/locales/en/auth.json';
import enSettings from '@/locales/en/settings.json';
import enProduction from '@/locales/en/production.json';
import enInventory from '@/locales/en/inventory.json';
import enOrders from '@/locales/en/orders.json';
import enProductDev from '@/locales/en/productDev.json';
import enProductSync from '@/locales/en/productSync.json';
import enOnboarding from '@/locales/en/onboarding.json';
import enClientOrders from '@/locales/en/clientOrders.json';
import enHelp from '@/locales/en/help.json';
import enLoading from '@/locales/en/loading.json';
import enBilling from '@/locales/en/billing.json';
import enManualOrders from '@/locales/en/manualOrders.json';
import enUploadStudio from '@/locales/en/uploadStudio.json';

const SUPPORTED_LANGS = ['en', 'zh'] as const;
export type SupportedLang = (typeof SUPPORTED_LANGS)[number];

export function isSupportedLang(v: unknown): v is SupportedLang {
  return typeof v === 'string' && SUPPORTED_LANGS.includes(v as SupportedLang);
}

const stored = localStorage.getItem('threadheads-language');
const initialLang = isSupportedLang(stored) ? stored : 'en';

// Set HTML lang attribute before first render to avoid one-frame font flash
document.documentElement.lang = initialLang === 'zh' ? 'zh-CN' : 'en';

// Use import.meta.glob for Vite compatibility (two dynamic segments in
// template literal imports fail Rollup's static analysis)
const localeModules = import.meta.glob('../locales/**/*.json');

i18next
  .use(resourcesToBackend((language: string, namespace: string) => {
    const key = `../locales/${language}/${namespace}.json`;
    const loader = localeModules[key];
    if (!loader) return Promise.reject(new Error(`No locale: ${key}`));
    return loader() as Promise<{ default: Record<string, string> }>;
  }))
  .use(initReactI18next)
  .init({
    lng: initialLang,
    fallbackLng: 'en',
    ns: ['common', 'nav', 'auth', 'settings', 'production', 'inventory', 'orders', 'productDev', 'productSync', 'onboarding', 'clientOrders', 'help', 'loading', 'billing', 'manualOrders', 'uploadStudio'],
    defaultNS: 'common',
    partialBundledLanguages: true,
    resources: {
      en: {
        common: enCommon,
        nav: enNav,
        auth: enAuth,
        settings: enSettings,
        production: enProduction,
        inventory: enInventory,
        orders: enOrders,
        productDev: enProductDev,
        productSync: enProductSync,
        onboarding: enOnboarding,
        clientOrders: enClientOrders,
        help: enHelp,
        loading: enLoading,
        billing: enBilling,
        manualOrders: enManualOrders,
        uploadStudio: enUploadStudio,
      },
    },
    react: { useSuspense: false },
    // escapeValue: false is safe because React escapes JSX output.
    // IMPORTANT: translation values must never contain HTML markup.
    interpolation: { escapeValue: false },
  });

export default i18next;
